import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { apiUrl } from '../config/api';
import ActionButton from '../components/UI/ActionButton';
import { formatDateDMY, formatDateTimeDMY } from '../utils/dateUtils';
import DisplayField from '../components/UI/DisplayField';

const SupportTicketPublic = () => {
  const { supportToken } = useParams();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);
  const [supportStatus, setSupportStatus] = useState('PENDING');
  const [supportUpdating, setSupportUpdating] = useState(false);
  const [supportMessage, setSupportMessage] = useState('');
  const [supportComment, setSupportComment] = useState('');
  const [supportPosting, setSupportPosting] = useState(false);
  const [comments, setComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [statusHistory, setStatusHistory] = useState([]);
  const [statusHistoryLoading, setStatusHistoryLoading] = useState(false);
  const [technicians, setTechnicians] = useState([]);
  const [technicianId, setTechnicianId] = useState('');

  const priorityTone = (p) => {
    const v = String(p || '').toUpperCase();
    if (v === 'HIGH') return 'red';
    if (v === 'LOW') return 'gray';
    return 'amber';
  };

  const slaChip = ({ dueAt, violatedAt, overdue }) => {
    if (violatedAt) {
      return {
        value: `Violated ${formatDateTimeDMY(violatedAt)}`,
        tone: 'red',
      };
    }
    if (!dueAt) return null;
    return {
      value: `Due ${formatDateTimeDMY(dueAt)}`,
      tone: overdue ? 'red' : 'gray',
    };
  };

  const fetchTechnicians = async () => {
    try {
      const r = await axios.get(apiUrl('/api/tickets/support/technicians'));
      const list = Array.isArray(r.data) ? r.data : [];
      setTechnicians(list);

      const storageKey = `supportTech:${supportToken}`;
      const saved = supportToken ? localStorage.getItem(storageKey) : '';
      if (saved && list.some((t) => String(t.id) === String(saved))) {
        setTechnicianId(String(saved));
        return;
      }
      const first = list.find((t) => t?.is_active && t?.receives_tickets !== false) || list.find((t) => t?.is_active);
      setTechnicianId(first?.id ? String(first.id) : '');
    } catch {
      setTechnicians([]);
      setTechnicianId('');
    }
  };

  useEffect(() => {
    const run = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await axios.get(apiUrl(`/api/tickets/support/${supportToken}`));
        setTicket(res.data);
        const currentStatus = String(res.data?.status || '').trim().toUpperCase();
        if (currentStatus) setSupportStatus(currentStatus);
      } catch (e) {
        setError(e?.response?.data?.error || e?.message || 'Failed to load ticket');
        setTicket(null);
      } finally {
        setLoading(false);
      }
    };
    if (supportToken) run();
  }, [supportToken]);

  useEffect(() => {
    if (supportToken) fetchTechnicians();
  }, [supportToken]);

  useEffect(() => {
    const run = async () => {
      setStatusHistoryLoading(true);
      try {
        const res = await axios.get(apiUrl(`/api/tickets/support/${supportToken}/status-changes`));
        setStatusHistory(Array.isArray(res.data) ? res.data : []);
      } catch {
        setStatusHistory([]);
      } finally {
        setStatusHistoryLoading(false);
      }
    };
    if (supportToken) run();
  }, [supportToken]);

  useEffect(() => {
    const run = async () => {
      setCommentsLoading(true);
      try {
        const res = await axios.get(apiUrl(`/api/tickets/support/${supportToken}/messages`));
        setComments(Array.isArray(res.data) ? res.data : []);
      } catch {
        setComments([]);
      } finally {
        setCommentsLoading(false);
      }
    };
    if (supportToken) run();
  }, [supportToken]);

  useEffect(() => {
    const run = async () => {
      setAttachmentsLoading(true);
      try {
        const res = await axios.get(apiUrl(`/api/tickets/support/${supportToken}/attachments`));
        setAttachments(Array.isArray(res.data) ? res.data : []);
      } catch {
        setAttachments([]);
      } finally {
        setAttachmentsLoading(false);
      }
    };
    if (supportToken) run();
  }, [supportToken]);

  const onSupportUpdate = async () => {
    if (!supportToken || !ticket?.id) return;
    setSupportMessage('');
    try {
      setSupportUpdating(true);
      await axios.post(apiUrl(`/api/tickets/support/${supportToken}/status`), {
        status: supportStatus,
      });
      setSupportMessage('Status updated.');
      const res = await axios.get(apiUrl(`/api/tickets/support/${supportToken}`));
      setTicket(res.data);
      const currentStatus = String(res.data?.status || '').trim().toUpperCase();
      if (currentStatus) setSupportStatus(currentStatus);
    } catch (e) {
      setSupportMessage(e?.response?.data?.error || e?.message || 'Failed to update status.');
    } finally {
      setSupportUpdating(false);
    }
  };

  const onSupportPostComment = async () => {
    if (!supportToken || !ticket?.id) return;
    const msg = String(supportComment || '').trim();
    if (!msg) return;
    setSupportMessage('');

    if (!technicianId) {
      setSupportMessage('Please select a technician before replying.');
      return;
    }

    try {
      setSupportPosting(true);
      await axios.post(apiUrl(`/api/tickets/support/${supportToken}/messages`), {
        message: msg,
        technicianId: Number(technicianId),
      });

      try {
        localStorage.setItem(`supportTech:${supportToken}`, String(technicianId));
      } catch {
        // ignore
      }

      setSupportComment('');
      const res = await axios.get(apiUrl(`/api/tickets/support/${supportToken}/messages`));
      setComments(Array.isArray(res.data) ? res.data : []);
      setSupportMessage('Comment added.');
    } catch (e) {
      setSupportMessage(e?.response?.data?.error || e?.message || 'Failed to add comment.');
    } finally {
      setSupportPosting(false);
    }
  };

  const viewAttachment = async (attachmentId, filename) => {
    if (!supportToken || !attachmentId) return;

    const newTab = window.open('', '_blank', 'noopener,noreferrer');
    try {
      const res = await axios.get(
        apiUrl(`/api/tickets/support/${supportToken}/attachments/${attachmentId}/download`),
        { responseType: 'blob' }
      );
      const blob = new Blob([res.data], {
        type: res.headers?.['content-type'] || 'application/octet-stream',
      });
      const url = window.URL.createObjectURL(blob);

      if (newTab && !newTab.closed) {
        newTab.location.href = url;
      } else {
        const a = document.createElement('a');
        a.href = url;
        a.target = '_blank';
        a.rel = 'noreferrer';
        a.textContent = 'View attachment';
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      window.URL.revokeObjectURL(url);
    } catch (e) {
      if (newTab && !newTab.closed) newTab.close();
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900">
      <div className="max-w-3xl mx-auto p-4 sm:p-6">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-2xl font-extrabold text-blue-800 dark:text-blue-400">Ticket</h1>
          <Link to="/login" className="text-blue-600 hover:underline">Login</Link>
        </div>

        {loading ? (
          <div className="bg-white dark:bg-slate-800 border dark:border-slate-700 rounded-lg p-4">Loading…</div>
        ) : error ? (
          <div className="bg-white dark:bg-slate-800 border dark:border-slate-700 rounded-lg p-4 text-red-700 dark:text-red-400">{error}</div>
        ) : (
          <div className="bg-white dark:bg-slate-800 border dark:border-slate-700 rounded-lg p-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div className="text-lg font-bold text-gray-900 dark:text-slate-100">#{ticket.id} · {ticket.subject}</div>
              <div className="text-sm text-gray-600 dark:text-slate-400">{ticket.kind} · {ticket.status}</div>
            </div>
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              <DisplayField label="Created">{ticket.created_at ? formatDateTimeDMY(ticket.created_at) : ''}</DisplayField>
              <DisplayField label="By" tone="blue">{ticket.created_by_email || ''}</DisplayField>
              <DisplayField label="Priority" tone={priorityTone(ticket.priority)}>{ticket.priority ? String(ticket.priority).toUpperCase() : ''}</DisplayField>
              <DisplayField label="Topic" tone="purple">{ticket.topic_name || ''}</DisplayField>
              <DisplayField label="SLA">{ticket.sla_level_name || ''}</DisplayField>
              {(() => {
                const s = String(ticket.status || '').toUpperCase();
                const isClosed = s === 'CLOSED' || s === 'CANCELLED';
                const dueAt = ticket.response_due_at;
                const overdue = !!dueAt && !ticket.first_response_at && !isClosed && (new Date(dueAt).getTime() < Date.now());
                const chip = slaChip({ dueAt, violatedAt: ticket.response_sla_violated_at, overdue });
                return chip ? <DisplayField label="Resp SLA" tone={chip.tone}>{chip.value}</DisplayField> : null;
              })()}
              {(() => {
                const s = String(ticket.status || '').toUpperCase();
                const isClosed = s === 'CLOSED' || s === 'CANCELLED';
                const dueAt = ticket.resolution_due_at;
                const overdue = !!dueAt && !isClosed && (new Date(dueAt).getTime() < Date.now());
                const chip = slaChip({ dueAt, violatedAt: ticket.resolution_sla_violated_at, overdue });
                return chip ? <DisplayField label="Res SLA" tone={chip.tone}>{chip.value}</DisplayField> : null;
              })()}
              <DisplayField label="Assigned" tone="green">{ticket.assigned_technician_name ? `${ticket.assigned_technician_name}${ticket.assigned_technician_email ? ' (' + ticket.assigned_technician_email + ')' : ''}` : ''}</DisplayField>
            </div>

            {supportToken && (
              <div className="mt-4 p-3 rounded-lg border border-amber-200 dark:border-amber-700/40 bg-amber-50 dark:bg-amber-900/20">
                <div className="font-semibold text-amber-900 dark:text-amber-300">Support actions</div>
                <div className="text-sm text-amber-900 dark:text-amber-400 mt-1">Support can update status and add comments without logging in.</div>

                <div className="flex flex-col sm:flex-row gap-2 mt-3">
                  <select
                    value={supportStatus}
                    onChange={(e) => setSupportStatus(e.target.value)}
                    className="w-full"
                    disabled={supportUpdating || String(ticket?.status || '').toUpperCase() === 'CLOSED'}
                  >
                    <option value="OPEN">OPEN</option>
                    <option value="PENDING">PENDING</option>
                    <option value="IN_PROGRESS">IN_PROGRESS</option>
                    <option value="CLOSED">CLOSED</option>
                    <option value="CANCELLED">CANCELLED</option>
                  </select>
                  <ActionButton
                    type="button"
                    onClick={onSupportUpdate}
                    className="px-4 py-2 rounded-lg bg-green-600 text-white disabled:opacity-60"
                    disabled={supportUpdating || String(ticket?.status || '').toUpperCase() === 'CLOSED'}
                  >
                    {supportUpdating ? 'Updating…' : 'Update status'}
                  </ActionButton>
                </div>

                {String(ticket?.status || '').toUpperCase() === 'CLOSED' && (
                  <div className="text-sm mt-2 text-amber-950 dark:text-amber-300">
                    This ticket is closed and cannot be reopened.
                  </div>
                )}

                <div className="mt-4">
                  <div className="text-sm font-semibold text-amber-900 dark:text-amber-300">Add comment</div>
                  <label className="block text-sm font-medium text-gray-800 dark:text-slate-200 mt-2 mb-1">Replying as</label>
                  <select
                    value={technicianId}
                    onChange={(e) => setTechnicianId(e.target.value)}
                    className={`w-full ${technicianId ? '' : 'border-rose-300 dark:border-rose-700/60 focus:border-rose-500 focus:ring-rose-500/20'}`}
                    disabled={supportPosting}
                  >
                    <option value="">Select technician…</option>
                    {technicians
                      .filter((t) => t?.is_active)
                      .map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name || 'Technician'}{t.email ? ` (${t.email})` : ''}
                        </option>
                      ))}
                  </select>
                  <textarea
                    value={supportComment}
                    onChange={(e) => setSupportComment(e.target.value)}
                    className={`mt-2 min-h-20 w-full ${supportComment.trim() ? '' : 'border-rose-300 dark:border-rose-700/60 focus:border-rose-500 focus:ring-rose-500/20'}`}
                    placeholder="Write a comment…"
                    disabled={supportPosting}
                  />
                  <ActionButton
                    type="button"
                    onClick={onSupportPostComment}
                    className="mt-2 px-4 py-2 rounded-lg bg-blue-700 text-white disabled:opacity-60"
                    disabled={supportPosting || !String(supportComment || '').trim()}
                  >
                    {supportPosting ? 'Posting…' : 'Post comment'}
                  </ActionButton>
                </div>

                {supportMessage && (
                  <div className="text-sm mt-2 text-amber-950 dark:text-amber-300">{supportMessage}</div>
                )}
              </div>
            )}

            <div className="mt-4">
              <div className="text-sm font-semibold text-gray-700 dark:text-slate-300 mb-1">Description</div>
              <div className="whitespace-pre-wrap text-gray-800 dark:text-slate-200 border dark:border-slate-600 rounded-lg p-3 bg-gray-50 dark:bg-slate-700">{ticket.description}</div>
            </div>

            <div className="mt-4">
              <div className="text-sm font-semibold text-gray-700 dark:text-slate-300 mb-1">Comments</div>
              {commentsLoading ? (
                <div className="text-sm text-gray-600 dark:text-slate-400">Loading comments…</div>
              ) : comments.length === 0 ? (
                <div className="text-sm text-gray-600 dark:text-slate-400">No comments yet.</div>
              ) : (
                <div className="space-y-2">
                  {comments.map((c) => (
                    <div key={c.id} className="border dark:border-slate-600 rounded-lg p-2 bg-gray-50 dark:bg-slate-700">
                      <div className="text-xs text-gray-500 dark:text-slate-400">{c.author_name || 'User'}{c.author_email ? ` (${c.author_email})` : ''} · {c.created_at ? formatDateTimeDMY(c.created_at) : '—'}</div>
                      <div className="text-sm text-gray-800 dark:text-slate-200 whitespace-pre-wrap">{c.message}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="mt-4">
              <div className="text-sm font-semibold text-gray-700 dark:text-slate-300 mb-1">Status change history</div>
              {statusHistoryLoading ? (
                <div className="text-sm text-gray-600 dark:text-slate-400">Loading status history…</div>
              ) : statusHistory.length === 0 ? (
                <div className="text-sm text-gray-600 dark:text-slate-400">No status changes yet.</div>
              ) : (
                <div className="space-y-2">
                  {statusHistory.map((h) => (
                    <div key={h.id} className="border dark:border-slate-600 rounded-lg p-2 bg-gray-50 dark:bg-slate-700">
                      <div className="text-xs text-gray-500 dark:text-slate-400">{h.changed_at ? formatDateTimeDMY(h.changed_at) : '—'}</div>
                      <div className="text-sm text-gray-800 dark:text-slate-200">
                        {String(h.from_status || '').toUpperCase()} → {String(h.to_status || '').toUpperCase()}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="mt-4">
              <div className="text-sm font-semibold text-gray-700 dark:text-slate-300 mb-1">Attachments</div>
              {attachmentsLoading ? (
                <div className="text-sm text-gray-600 dark:text-slate-400">Loading attachments…</div>
              ) : attachments.length === 0 ? (
                <div className="text-sm text-gray-600 dark:text-slate-400">No attachments.</div>
              ) : (
                <div className="space-y-2">
                  {attachments.map((a) => (
                    <div key={a.id} className="flex items-center justify-between gap-2 border dark:border-slate-600 rounded-lg p-2 bg-gray-50 dark:bg-slate-700">
                      <div className="text-sm text-gray-800 dark:text-slate-200 truncate">
                        {a.filename}
                        {a.file_size_bytes ? <span className="text-xs text-gray-500 dark:text-slate-400"> · {Math.round(Number(a.file_size_bytes) / 1024)} KB</span> : null}
                      </div>
                      <ActionButton
                        type="button"
                        className="px-2.5 py-1 text-xs font-medium rounded-md transition-colors bg-blue-50 text-blue-600 hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-300 whitespace-nowrap"
                        onClick={() => viewAttachment(a.id, a.filename)}
                      >
                        View
                      </ActionButton>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="mt-4 text-xs text-gray-500 dark:text-slate-500">
              This link is intended for support use only.
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SupportTicketPublic;
